/**
 * Global QueryClient configuration for proper abort/cancellation handling.
 *
 * Handles:
 * - AbortError: retry with backoff (mend connection after GoTrue / nav races)
 * - Retry logic for real failures
 * - AbortError is never surfaced to global onError handlers
 */
import { QueryClient } from "react-query";
import { isAbortError } from "../errors/isAbortError";
import {
  SUPABASE_ABORT_RETRY_ATTEMPTS,
  abortRetryDelayMs,
} from "../errors/supabaseAbortRetry";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,

      retry: (failureCount, error: unknown) => {
        // Supabase AbortError (code 20) from auth/nav races — retry so pages
        // remend the connection instead of sticking on skeletons.
        if (isAbortError(error)) {
          return failureCount < SUPABASE_ABORT_RETRY_ATTEMPTS;
        }
        // Don't retry 4xx client errors
        const err = error as { status?: number };
        if (typeof err?.status === "number" && err.status >= 400 && err.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors (network, 5xx, etc.)
        return failureCount < 2;
      },

      retryDelay: (failureCount, error: unknown) => {
        if (isAbortError(error)) {
          return abortRetryDelayMs(failureCount);
        }
        // Default RQ-ish backoff for other errors
        return Math.min(1000 * 2 ** Math.max(0, failureCount - 1), 8000);
      },

      onError: (error: unknown) => {
        // Never surface AbortError to global handlers (toasts, etc.)
        if (isAbortError(error)) {
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.error("React Query error:", error);
        }
      },
    },
  },
});
